import { useQueryClient, useMutation } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { usePendingOfferPollingStore } from '@/lib/offerStatusPolling/usePendingOfferPollingStore';
import { useRefetchStatsQueries } from '@/lib/serverSentEvents/useServerSideEventListeners';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import useTakerMarkets, { TakerMarket } from './useTakerMarkets';

type Referrer = paths['/offers/express-accept']['post']['requestBody']['content']['application/json']['referrer'];

export interface SubmitExpressAcceptOfferParams {
  rate: number;
  rateType: 'APR' | 'DISC';
  makerOrganizationUuid: string;
  marketUuid: string;
  supplierDivisionUuid: string;
  referrer?: Referrer;
}

const useSubmitExpressAcceptOffer = () => {
  const { addError } = useDataDog();
  const { trackEnterpriseEvent } = useReporting();
  const queryClient = useQueryClient();
  const { data: takerMarkets } = useTakerMarkets();
  const { refetchOneTakerMarket } = useRefetchStatsQueries();
  const { addPendingMarkets } = usePendingOfferPollingStore();

  return useMutation({
    mutationKey: ['useSubmitExpressAcceptOffer'],
    mutationFn: async (variables: SubmitExpressAcceptOfferParams) => {
      const { rate, rateType, makerOrganizationUuid, marketUuid, supplierDivisionUuid, referrer } = variables;

      const response = await apiClient.post('api/supplier-experience/offers/express-accept', {
        json: {
          groupingKey: 'SUPPLIER_MARKET',
          offerType: 'express-accept',
          frequency: 'one-time',
          rateType,
          rate,
          supplierMarkets: [
            {
              makerOrganizationUuid,
              marketUuid,
              supplierDivisionUuid,
            },
          ],
          referrer,
        } satisfies paths['/offers/express-accept']['post']['requestBody']['content']['application/json'],
      });

      if (response.status === 201) {
        const body =
          await response.json<
            paths['/offers/express-accept']['post']['responses']['201']['content']['application/json']
          >();
        return { status: response.status, offerUuid: body.data[0]?.uuid };
      }

      if (response.status === 202) {
        const body =
          await response.json<
            paths['/offers/express-accept']['post']['responses']['202']['content']['application/json']
          >();
        return { status: response.status, offerUuid: body.data.offers[0]?.uuid };
      }

      return { status: response.status };
    },
    onSuccess: (result, variables) => {
      const tmMeta = takerMarkets?.find(
        (meta) => meta.marketUuid === variables.marketUuid && meta.takerDivisionUuid === variables.supplierDivisionUuid
      );

      if (tmMeta?.marketUuid && tmMeta?.takerDivisionId) {
        // If placing first offer, use returned offer uuid from API response to update cache
        // User can interact with offer before stats and offer refetches
        const offerUuid = tmMeta?.offerConfig.uuid ?? result.offerUuid;

        // update the cache with the returned data
        queryClient.setQueryData<TakerMarket[]>(['taker-markets'], (prevTakerMarkets) =>
          (prevTakerMarkets ?? []).map((tm) => ({
            ...(tm.marketUuid === variables.marketUuid && tm.takerDivisionUuid === variables.supplierDivisionUuid
              ? {
                  ...tm,
                  offerConfig: {
                    ...tm.offerConfig,
                    isDiscountBidding: variables.rateType === 'DISC',
                    maxDiscount: variables.rateType === 'DISC' ? variables.rate : tm.offerConfig.maxDiscount,
                    maxApr: variables.rateType === 'APR' ? variables.rate : tm.offerConfig.maxApr,
                    isEnabled: true,
                    uuid: offerUuid,
                    offerType: 'express-accept',
                    isManuallyAwarded: true,
                  },
                  disableAfterMarketClearsDate: null,
                  offerSchedule: undefined,
                }
              : tm),
          }))
        );

        if (result.status === 202) {
          addPendingMarkets([
            {
              marketUuid: tmMeta.marketUuid,
              takerDivisionUuid: tmMeta.takerDivisionUuid,
              makerOrganizationUuid: tmMeta.makerOrganizationUuid,
            },
          ]);
        } else {
          // offer resolved synchronously, refetch immediately
          trackEnterpriseEvent('polling-update::in-time', {
            marketUuid: tmMeta.marketUuid,
            takerDivisionUuid: tmMeta.takerDivisionUuid,
            makerOrganizationUuid: tmMeta.makerOrganizationUuid,
          });
          refetchOneTakerMarket({ marketUuid: tmMeta.marketUuid, takerId: tmMeta.takerDivisionId });
        }
      }
    },
    onError: (error, variables) => {
      const takerMarket = takerMarkets?.find(
        (meta) => meta.marketUuid === variables.marketUuid && meta.takerDivisionUuid === variables.supplierDivisionUuid
      );

      addError('useSubmitExpressAcceptOffer', {
        error,
        variables,
        seaOfferUuid: takerMarket?.offerConfig.uuid,
      });
    },
  });
};

export default useSubmitExpressAcceptOffer;
